import { useSetState } from '@/shared/lib/customHooks';
import { initialInsurance } from '../../lib/patientDetailsTab';
import { PATIENT_DETAIL_KEY } from '../../config/patientDetailsTab';
import type { InsuranceDrawerProps } from './InsuranceDrawer.types';

/**
 * Holds the editable insurance form of the study's patient details drawer. Seeds merged state from
 * the study's insurance data and wires a field change handler plus the submit that passes the
 * insurance payload to handleClick.
 *
 * @param props - Study data and the parent save callback handleClick.
 * @returns An object carrying the form state, the field change handler and the submit handler.
 */
export const useInsuranceDrawer = ({
  data,
  handleClick,
}: Pick<InsuranceDrawerProps, 'data' | 'handleClick'>) => {
  const [state, setState] = useSetState<any>({
    ...initialInsurance(data),
  });

  /**
   * Records an edit to one insurance drawer field. Writes the value into merged state under the field name.
   *
   * @param name - Field being edited.
   * @param value - New field value.
   * @returns Nothing.
   */
  const handleChange = (name: any, value: any) => {
    setState({ [name]: value });
  };

  /**
   * Saves the patient's insurance details entered in the drawer. Assembles the insurance type, the trimmed provider and the pre-auth value and hands them to the parent save callback under the insurance key with a completed status.
   *
   * @returns Nothing.
   */
  const onClickBottomButton = () => {
    const payload = {
      insurance: {
        type: state?.type,
        provider: state?.provider?.trim(),
        preAuth: state?.preAuth,
      },
    };
    handleClick({
      payload,
      status: true,
      key: PATIENT_DETAIL_KEY.INSURANCE,
    });
  };

  return { state, handleChange, onClickBottomButton };
};
